import Card from "@/components/Card";
import InputError from "@/components/InputError";
import SkeletonTable from "@/components/SkeletonTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/axios";
import { getDataVerifikasiSehat, getMasterSystem } from "@/services/getData";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Activity, Ban, Check, Info, Loader2, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useSearchParams } from "react-router-dom";

type ValidationState = {
  id_data_pendaftaran: string;
  id_data_berkas_siswa: string;
  is_valid: boolean;
  catatan_verifikator: string;
};

const patchDataValidate = async (form: ValidationState) => {
  const response = await api.patch(
    `/verifikator/peserta-surat-keterangan-sehat/validate`,
    form,
  );
  return response.data;
};

const patchDataReset = async ({
  id_data_pendaftaran,
  id_data_berkas_siswa,
}: {
  id_data_pendaftaran: string;
  id_data_berkas_siswa: string;
}) => {
  const response = await api.patch(
    `/verifikator/peserta-surat-keterangan-sehat/reset`,
    {
      id_data_pendaftaran,
      id_data_berkas_siswa,
    },
  );
  return response.data;
};

export default function VerifikasiPesertaSehatDetailPage() {
  const [searchParams] = useSearchParams();
  const nomorPendaftaranParams = searchParams.get("no") || "";

  const [form, setForm] = useState<ValidationState>({
    id_data_pendaftaran: "",
    id_data_berkas_siswa: "",
    is_valid: false,
    catatan_verifikator: "",
  });

  const { data, isPending } = useQuery({
    queryKey: ["dataBerkasSehat"],
    queryFn: () => getDataVerifikasiSehat(nomorPendaftaranParams),
    enabled: !!nomorPendaftaranParams,
  });

  const { data: system } = useQuery({
    queryKey: ["masterSystem"],
    queryFn: getMasterSystem,
  });

  const isWaktuSanggahBerkas =
    system && system[0]?.is_waktu_sanggah_berkas_daftarkembali;

  const queryClient = useQueryClient();
  const {
    mutate,
    isPending: isPendingValidate,
    error,
  } = useMutation({
    mutationFn: patchDataValidate,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataBerkasSehat"],
      });
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const { mutate: mutateResetValidation, isPending: isPendingResetValidation } =
    useMutation({
      mutationFn: patchDataReset,
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["dataBerkasSehat"],
        });
        setForm((prev) => ({ ...prev, catatan_verifikator: "" }));
        toast.success(data?.message || "Success.");
      },
      onError: (error: AxiosError) => {
        const err = error as AxiosError<{ message?: string }>;
        toast.error(
          err?.response?.data?.message || "An unexpected error occurred.",
        );
      },
    });

  const handleValidate = (is_valid: boolean) => {
    mutate({
      ...form,
      id_data_pendaftaran:
        String(data?.dataPendaftaran?.id_data_pendaftaran) || "",
      id_data_berkas_siswa: data?.dataBerkasSehat?.id_data_berkas_siswa || "",
      is_valid,
    });
  };

  const handleReset = () => {
    mutateResetValidation({
      id_data_pendaftaran:
        String(data?.dataPendaftaran?.id_data_pendaftaran) || "",
      id_data_berkas_siswa: data?.dataBerkasSehat?.id_data_berkas_siswa || "",
    });
  };

  useEffect(() => {
    if (!data?.dataPendaftaran) return;
    setForm((prev) => ({
      ...prev,
      catatan_verifikator: data?.dataBerkasSehat?.catatan_verifikator ?? "",
    }));
  }, [data]);

  if (isPending) return <SkeletonTable />;

  return (
    <div className="flex flex-col h-full min-h-screen bg-background dark:bg-[#09090b] text-foreground transition-colors duration-200 p-4 md:p-6">
      {/* --- HEADER PAGE & TOMBOL KEMBALI --- */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Link
            to={
              "/verifikator/surat-keterangan-sehat/verifikasi-peserta-surat-keterangan-sehat"
            }
            className="inline-flex items-center justify-center rounded-lg border border-border bg-card dark:border-zinc-800 dark:bg-zinc-900 hover:bg-muted dark:hover:bg-zinc-800 text-muted-foreground hover:text-foreground h-10 w-10 transition-colors shadow-sm"
            title="Kembali"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Detail Verifikasi Surat Sehat
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Periksa kecocokan data biodata dengan dokumen hasil pemeriksaan
              kesehatan.
            </p>
          </div>
        </div>
      </div>

      <section className="flex-1 grid grid-cols-1 xl:grid-cols-12 gap-6 xl:h-[calc(100vh-160px)]">
        {/* --- KOLOM KIRI: PREVIEW DOKUMEN (Span 7) --- */}
        <div className="xl:col-span-7 flex flex-col h-[70vh] xl:h-full border border-border dark:border-zinc-800 rounded-xl overflow-hidden bg-card shadow-sm relative">
          <div className="z-10 p-4 border-b border-border dark:border-zinc-800 bg-muted/30 dark:bg-zinc-900/50 backdrop-blur-md">
            <h1 className="font-bold text-lg sm:text-xl flex items-center gap-2">
              <span className="p-1.5 bg-primary/10 rounded text-primary">
                <Activity className="w-5 h-5" />
              </span>
              {data?.dataBerkasSehat?.sys_berkas?.keterangan_berkas ||
                "Preview Dokumen"}
              {isWaktuSanggahBerkas ? (
                <Badge variant="destructive" className="ml-2 animate-pulse">
                  Mode Sanggah
                </Badge>
              ) : (
                ""
              )}
            </h1>
          </div>
          <div className="flex-1 relative bg-zinc-100 dark:bg-zinc-900">
            <iframe
              src={
                isWaktuSanggahBerkas
                  ? data?.dataBerkasSehat?.path_sanggah_berkas
                  : data?.dataBerkasSehat?.path_berkas || ""
              }
              title="Surat Keterangan Sehat"
              className="absolute inset-0 h-full w-full border-0"
            />
          </div>
        </div>

        {/* --- KOLOM KANAN: BIODATA & VERIFIKASI (Span 5) --- */}
        <div className="xl:col-span-5 flex flex-col gap-6 overflow-y-auto pr-1 custom-scrollbar h-full">
          {/* CARD BIODATA */}
          <Card className="border-border dark:border-zinc-800 bg-card shadow-sm overflow-hidden">
            <div className="p-4 border-b border-border dark:border-zinc-800 bg-muted/20">
              <h2 className="font-bold text-lg flex items-center gap-2">
                <Info className="w-5 h-5 text-muted-foreground" />
                Biodata Peserta
              </h2>
            </div>
            <div className="p-0">
              <div className="divide-y divide-border dark:divide-zinc-800">
                {[
                  {
                    label: "Nama Lengkap",
                    value: data?.dataPendaftaran?.data_siswa?.nama_lengkap,
                  },
                  {
                    label: "Jenis Kelamin",
                    value:
                      data?.dataPendaftaran?.data_siswa?.jenis_kelamin === "P"
                        ? "Perempuan"
                        : data?.dataPendaftaran?.data_siswa?.jenis_kelamin ===
                            "L"
                          ? "Laki-Laki"
                          : "-",
                  },
                  {
                    label: "Nomor Telepon",
                    value: data?.dataPendaftaran?.data_siswa?.handphone,
                  },
                  {
                    label: "Tanggal Lahir",
                    value: data?.dataPendaftaran?.data_siswa?.tanggal_lahir,
                  },
                  {
                    label: "Tinggi / Berat",
                    value: `${data?.dataPendaftaran?.data_siswa?.tinggi_badan ?? 0} cm / ${data?.dataPendaftaran?.data_siswa?.berat_badan ?? 0} kg`,
                  },
                  {
                    label: "No. Pendaftaran",
                    value: (
                      <span className="font-mono text-primary font-semibold">
                        {data?.dataPendaftaran?.nomor_pendaftaran}
                      </span>
                    ),
                  },
                  {
                    label: "Jalur Pendaftaran",
                    value:
                      data?.dataPendaftaran?.periode?.jalur_jenis
                        ?.nama_jalur_jenis,
                  },
                  {
                    label: "Program Studi",
                    value: data?.dataPendaftaran?.unitkerja?.nama,
                  },
                  {
                    label: "Status Validasi",
                    value: (
                      <Badge
                        variant="outline"
                        className={
                          data?.dataBerkasSehat?.is_valid === null
                            ? "bg-amber-500/10 text-amber-600 border-amber-500/20"
                            : data?.dataBerkasSehat?.is_valid
                              ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                              : "bg-destructive/10 text-destructive border-destructive/20"
                        }
                      >
                        {data?.dataBerkasSehat?.is_valid === null
                          ? "Menunggu Validasi"
                          : data?.dataBerkasSehat?.is_valid
                            ? "Valid"
                            : "Tidak Valid"}
                      </Badge>
                    ),
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex flex-col sm:flex-row sm:items-center py-3 px-4 hover:bg-muted/30 transition-colors"
                  >
                    <span className="text-sm text-muted-foreground sm:w-1/3 mb-1 sm:mb-0">
                      {item.label}
                    </span>
                    <span className="text-sm font-medium sm:w-2/3">
                      {item.value || "-"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* CARD PANEL VERIFIKATOR */}
          <Card className="border-border dark:border-zinc-800 bg-card shadow-sm border-t-4 border-t-primary">
            <div className="p-5 space-y-5">
              {/* Input: Textarea Catatan */}
              <div className="space-y-2">
                <Label className="text-sm font-bold text-foreground">
                  Catatan Verifikasi
                </Label>
                <Textarea
                  placeholder="Ketik catatan jika dokumen tidak valid atau ada keterangan lain..."
                  className="min-h-[100px] bg-muted/20 focus-visible:ring-primary/50 dark:border-zinc-700 resize-none text-foreground"
                  value={form.catatan_verifikator}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      catatan_verifikator: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data.errors?.catatan_verifikator?.join(
                    ", ",
                  )}
                />
              </div>

              {/* AREA TOMBOL AKSI */}
              <div className="pt-2">
                {/* Jika sudah divalidasi -> Tampilkan Badge Hasil & Tombol Reset */}
                {data?.dataBerkasSehat?.is_valid !== null ? (
                  <div className="flex flex-col gap-4 p-4 rounded-lg bg-primary/5 border border-primary/10">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">
                        Hasil validasi anda:
                      </span>
                      <Badge
                        variant={
                          data?.dataBerkasSehat?.is_valid
                            ? "default"
                            : "destructive"
                        }
                        className="px-3 py-1 font-bold shadow-sm"
                      >
                        {data?.dataBerkasSehat?.is_valid
                          ? "VALID"
                          : "TIDAK VALID"}
                      </Badge>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full h-10 border-dashed dark:border-zinc-700 bg-background hover:bg-muted"
                      onClick={handleReset}
                      disabled={isPendingResetValidation}
                    >
                      {isPendingResetValidation ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <RotateCcw className="mr-2 h-4 w-4" /> Reset Keputusan
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  /* Jika Belum Divalidasi -> Tombol Valid / Tidak Valid */
                  <div className="grid grid-cols-2 gap-4">
                    <Button
                      variant="outline"
                      className="h-12 border-destructive/30 text-destructive hover:bg-destructive hover:text-white transition-all shadow-sm font-semibold"
                      onClick={() => handleValidate(false)}
                      disabled={isPendingValidate}
                    >
                      {isPendingValidate ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Ban className="mr-2 h-5 w-5" /> Tidak Valid
                        </>
                      )}
                    </Button>
                    <Button
                      className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white transition-all shadow-md font-semibold"
                      onClick={() => handleValidate(true)}
                      disabled={isPendingValidate}
                    >
                      {isPendingValidate ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Check className="mr-2 h-5 w-5" /> Tandai Valid
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}
